
import { Component, Vue, Watch } from 'vue-property-decorator';
import { Getter, Mutation } from 'vuex-class';
import { showNotification, showServerError } from '@/utils';
import { eventBus } from "@/eventbus";
import PageTabs from "@/components/base/PageTabs.vue";
import Status from "@/components/base/Status.vue";
import DownloadBtn from "@/components/base/buttons/DownloadBtn.vue";
import { IUserV3 } from "@/api/types/user";
import { getRoles } from "@/api/auth";
import { changeUserPassword, getUserEntityV3, getUserV3 } from "@/api/user";
import UserMain from '@/components/administration/users/UserMain.vue';
import ActiveTokensCardTable from '@/components/administration/users/ActiveTokensCardTable.vue';
import { MESSENGER } from '@/mappings/messengers';
import router from "@/router";
import { ACCOUNT_TYPE } from "@/mappings/account-type";
import { setWindowTitle } from "@/services/TablePage/reportPreset";
import PageLayout from '@/components_v3/layout/PageLayout.vue';
import BaseButton from '@/components-UI/BaseButton.vue';
import SvgReload from "@/assets/icons/reload-double.svg";
import FormModal from '@/components/base/FormModal.vue';
import BaseInput from '@/components-UI/BaseInput.vue';

@Component({
    components: {
        DownloadBtn,
        Status,
        PageTabs,
        PageLayout,
        BaseButton,
        SvgReload,
        FormModal,
        BaseInput,
    },
})

export default class User extends Vue {
    @Mutation('SET_DEFAULT_FILTERS_PARAMS_OBJECT') setDefaultParamsObject;
    @Mutation('SET_FILTERS_PARAMS_OBJECT') setFiltersParamsObject;
    @Getter('GET_FILTERS_PARAMS_OBJECT') getFiltersParamsObject;
    @Getter('GET_DEFAULT_FILTERS_PARAMS_OBJECT') getDefaultFiltersParamsObject;

    user = {} as IUserV3;
    tabItems = [
        { tab: 'Основное', component: UserMain, class: 'tab-main' },
        { tab: 'Токены', component: ActiveTokensCardTable, class: 'tab-token' },
    ];
    tab: number = 0;
    isShowUser = false;
    showPasswordChangeModal = false;
    newPasswordVal: string = '';

    private readonly newPasswordRegex =
        /^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?~`\u00A7\u00B1\u2116]+$/;
    private readonly cyrillicRegex = /[\u0400-\u04FF]/;

    get isNewPasswordValid(): boolean {
        if (!this.newPasswordVal) return true;
        const v = this.newPasswordVal;
        const len = v.length;
        const noCyrillic = !this.cyrillicRegex.test(v);
        return len >= 8 && len <= 16 && noCyrillic && this.newPasswordRegex.test(v);
    }

    get isShowBodyComponent(): boolean {
        return Object.keys(this.user).length > 0 && this.isShowUser;
    }

    get bodyComponent(): any {
        return this.tabItems[this.tab].component;
    }

    get tabClass(): string {
        return this.tabItems[this.tab].class;
    }

    get title(): string {
        return `${this.user.account_id || this.user.id} ${this.user.first_name} ${this.user.last_name}`;
    }

    async created(): Promise<void> {
        this.setDefaultParamsObject({});
        this.setFiltersParamsObject({});
        await this.getUser();
        this.tab = Number(this.$route.query.tab!) || 0;
        this.changeTab(this.tab, true);
        eventBus.$on("update-user", this.getUser);
        setWindowTitle(`${this.user.first_name} ${this.user.last_name}`);
        this.$router.afterEach((to) => {
            if (to.name === 'user-detail-page') {
                setWindowTitle(`${this.user.first_name} ${this.user.last_name}`);
            }
        });
    }

    async getUser(): Promise<void> {
        try {
            await this.getUserData();
            this.user.available = await getRoles();
        } catch (err) {
            showServerError(err, 'Пользователь не найден');
        } finally {
            this.isShowUser = true;
        }
    }

    changeTab(tab: number, isHistory: boolean): void {
        this.tab = tab;
        if (isHistory) {
            this.setTabToUrl(this.$route.query);
        }
    }

    async handleClickSaveNewPassword(): Promise<void> {
        try {
            const { account_type, id } = this.user;
            
            if (account_type && id) {
                await changeUserPassword(account_type, id!, this.newPasswordVal);
                this.showPasswordChangeModal = false;
                this.newPasswordVal = '';
                showNotification('Пароль успешно изменён.');
            }
        } catch (e) {
            showServerError(e, 'Не удалось сменить пароль.');
        }
    }

    @Watch('$route.query')
    setTabToUrl(query?: object): void {
        if (this.$route.query.hasOwnProperty('tab') && Number(this.$route.query.tab!) === this.tab) {
            return;
        }
        const params = { ...query, ...{ tab: this.tab } };
        this.updateQueryString(params);

    }

    updateQueryString(data: any): void {
        const query = { ...data };
        router.replace({ name: router.currentRoute.name || '', query }).catch((error) => {
            if (error.name !== 'NavigationDuplicated') {
                throw error;
            }
        });
    }

    beforeDestroy(): void {
        eventBus.$off("update-user", this.getUser);
    }

    async getUserData(): Promise<void> {
        const rawType = this.$route.query.account_type;
        const accountType = Array.isArray(rawType) ? rawType[0] : rawType;
        const accountTypeStr = accountType != null && accountType !== '' ? String(accountType) : '';
        if (!accountTypeStr) {
            throw new Error('Не указан account_type в адресе страницы');
        }
        const isEmployeeOrAffiliate = ['employee', 'affiliate'].includes(accountTypeStr);
        if (isEmployeeOrAffiliate) {
            const id = Number(this.$route.params.id);
            this.user = await getUserV3(id, accountTypeStr as 'employee' | 'affiliate');
        } else {
            const id = Number(this.$route.query.id);
            this.user = await getUserEntityV3(id);
        }
        this.user.account_type = accountTypeStr as ACCOUNT_TYPE;
        if (this.user.messengers?.length === 0) {
            this.user.messengers = [
                { type: MESSENGER.TELEGRAM, account: '' },
                { type: MESSENGER.WHATSAPP, account: '' },
                { type: MESSENGER.SKYPE, account: '' },
            ];
        }
    }
}
